<template>
    <div id="contact">
        <!-- HERO -->
        <section class="contact-hero">
            <div class="hero-bg"></div>
            <div class="hero-overlay"></div>
            <div class="hero-grain"></div>

            <div class="hero-top">
                <span class="hero-brand">AGH SOLAR BOAT</span>
                <span class="hero-divider"></span>
                <span class="hero-tag">{{ $t('contact.page.tag') }}</span>
                <span class="hero-divider hero-divider--hide"></span>
                <span class="hero-since hero-since--hide">{{ $t('contact.page.since') }}</span>
            </div>

            <div class="hero-content">
                <span class="hero-eyebrow">{{ $t('contact.page.eyebrow') }}</span>
                <h1 class="hero-title">{{ $t('contact.line_1') }}</h1>
                <p class="hero-lead">{{ $t('contact.line_2') }}</p>

                <a href="mailto:kontakt@solarboat.agh.edu.pl" class="hero-mail-cta">
                    <span>kontakt@solarboat.agh.edu.pl</span>
                    <span class="cta-arrow">→</span>
                </a>
            </div>

            <div class="hero-bottom">
                <a href="#coordinators" class="scroll-link">
                    <span>{{ $t('contact.page.scroll_coordinators') }}</span>
                    <span class="scroll-arrow">↓</span>
                </a>
            </div>
        </section>

        <!-- COORDINATORS -->
        <section id="coordinators" class="block-light">
            <v-container class="block-container">
                <div class="section-meta">
                    <span class="chapter-num">01</span>
                    <span class="chapter-divider"></span>
                    <span class="chapter-eyebrow">{{ $t('contact.page.coordinators_eyebrow') }}</span>
                </div>

                <div class="coords-header">
                    <h2 class="block-title" v-html="$t('contact.page.coordinators_title')"></h2>
                    <p class="coords-lead">
                        {{ $t('contact.page.coordinators_lead') }}
                    </p>
                </div>

                <div class="coords-grid">
                    <div
                        v-for="coo in allCoordinators"
                        :key="coo.id"
                        class="coord-card">
                        <div class="coord-photo">
                            <img :src="coo.photo" :alt="coo.name" />
                        </div>
                        <div class="coord-info">
                            <span class="coord-role">{{ coo.role }}</span>
                            <h3 class="coord-name">{{ coo.name }}</h3>
                            <a :href="'mailto:' + coo.mail" class="coord-link">{{ coo.mail }}</a>
                            <a v-if="coo.tel" :href="'tel:' + coo.tel.replace(/\s/g, '')" class="coord-tel">{{ coo.tel }}</a>
                        </div>
                    </div>
                </div>
            </v-container>
        </section>

        <!-- DIRECT CONTACT -->
        <section class="block-dark">
            <v-container class="block-container">
                <div class="section-meta section-meta--light">
                    <span class="chapter-num">02</span>
                    <span class="chapter-divider"></span>
                    <span class="chapter-eyebrow">{{ $t('contact.page.direct_eyebrow') }}</span>
                </div>

                <h2 class="block-title block-title--light" v-html="$t('contact.page.direct_title')"></h2>

                <div class="contact-grid">
                    <div class="contact-card">
                        <span class="card-label">{{ $t('contact.page.mail_label') }}</span>
                        <a href="mailto:kontakt@solarboat.agh.edu.pl" class="card-value card-link">
                            kontakt@solarboat.agh.edu.pl
                        </a>
                        <p class="card-note">{{ $t('contact.page.mail_note') }}</p>
                    </div>

                    <div class="contact-card">
                        <span class="card-label">{{ $t('contact.page.address_label') }}</span>
                        <span class="card-value">
                            {{ $t('contact.page.address_university') }}<br>
                            {{ $t('contact.page.address_street') }}<br>
                            {{ $t('contact.page.address_city') }}
                        </span>
                        <p class="card-note">{{ $t('contact.page.address_note') }}</p>
                    </div>

                    <div class="contact-card">
                        <span class="card-label">{{ $t('contact.page.social_label') }}</span>
                        <div class="card-socials">
                            <a href="https://www.facebook.com/aghsolarboat" target="_blank" rel="noopener noreferrer" class="card-link">Facebook</a>
                            <a href="https://www.instagram.com/aghsolarboat/" target="_blank" rel="noopener noreferrer" class="card-link">Instagram</a>
                            <a href="https://www.linkedin.com/company/agh-solar-boat-team" target="_blank" rel="noopener noreferrer" class="card-link">LinkedIn</a>
                            <a href="https://www.youtube.com/@aghsolarboatteam" target="_blank" rel="noopener noreferrer" class="card-link">YouTube</a>
                        </div>
                        <p class="card-note">{{ $t('contact.page.social_note') }}</p>
                    </div>
                </div>
            </v-container>
        </section>
    </div>
</template>

<script>
    export default {
        name: "ContentComponent",
        data() {
            const photos = {
                'Michał Zawada': require('@/assets/img/components/team/team_photos/Michal Zawada.webp'),
                'Kinga Rokosz': require('@/assets/img/components/team/team_photos/Kinga Rokosz.webp'),
                'Kacper Ćwiokowski': require('@/assets/img/components/team/team_photos/Kacper Cwiokowski.webp'),
                'Karolina Machowska': require('@/assets/img/components/team/team_photos/Karolina Machowska.webp'),
                'Oskar Antonik': require('@/assets/img/components/team/team_photos/Oskar Antonik.webp'),
                'Dawid Stęposz': require('@/assets/img/components/team/team_photos/Dawid Steposz.webp'),
                'Hubert Kraj': require('@/assets/img/components/team/team_photos/Hubert Kraj.webp'),
                'Filip Nawalaniec': require('@/assets/img/components/team/team_photos/Filip Nawalaniec.webp'),
                'Julia Czekańska': require('@/assets/img/components/team/team_photos/Julia Czekanska.webp'),
                'Anita Uzar': require('@/assets/img/components/team/team_photos/Anita Uzar.webp')
            }

            const contacts = {
                'Michał Zawada': { tel: '+48 880 510 731', mail: 'michal.zawada@solarboat.agh.edu.pl' },
                'Kinga Rokosz': { tel: '+48 570 640 133', mail: 'kinga.rokosz@solarboat.agh.edu.pl' },
                'Kacper Ćwiokowski': { tel: '+48 729 273 460', mail: 'kacper.cwiokowski@solarboat.agh.edu.pl' },
                'Karolina Machowska': { tel: '+48 570 540 249', mail: 'karolina.machowska@solarboat.agh.edu.pl' },
                'Oskar Antonik': { mail: 'oskar.antonik@solarboat.agh.edu.pl' },
                'Dawid Stęposz': { mail: 'dawid.steposz@solarboat.agh.edu.pl' },
                'Hubert Kraj': { tel: '+48 883 938 798', mail: 'hubert.kraj@solarboat.agh.edu.pl' },
                'Filip Nawalaniec': { mail: 'filip.nawalaniec@solarboat.agh.edu.pl' },
                'Julia Czekańska': { tel: '+48 795 752 512', mail: 'julia.czekanska@solarboat.agh.edu.pl' },
                'Anita Uzar': { tel: '+48 730 580 858', mail: 'anita.uzar@solarboat.agh.edu.pl' }
            }

            const teamSections = [
                {
                    key: 'main',
                    coordinators: [
                        { name: 'Michał Zawada', roleKey: 'main_coordinator' },
                        { name: 'Kinga Rokosz', roleKey: 'vice_main_coordinator' }
                    ]
                },
                {
                    key: 'construction',
                    coordinators: [
                        { name: 'Kacper Ćwiokowski', roleKey: 'section_coordinator' }
                    ]
                },
                {
                    key: 'composites',
                    coordinators: [
                        { name: 'Karolina Machowska', roleKey: 'section_coordinator_f' },
                        { name: 'Oskar Antonik', roleKey: 'section_coordinator' }
                    ]
                },
                {
                    key: 'electricity',
                    coordinators: [
                        { name: 'Dawid Stęposz', roleKey: 'section_coordinator' },
                        { name: 'Hubert Kraj', roleKey: 'section_coordinator' }
                    ]
                },
                {
                    key: 'autonomic',
                    coordinators: [
                        { name: 'Filip Nawalaniec', roleKey: 'section_coordinator' },
                        { name: 'Julia Czekańska', roleKey: 'section_coordinator_f' }
                    ]
                },
                {
                    key: 'marketing',
                    coordinators: [
                        { name: 'Anita Uzar', roleKey: 'section_coordinator_f' }
                    ]
                }
            ]

            return {
                teamSections,
                photos,
                contacts
            }
        },
        computed: {
            allCoordinators() {
                return this.teamSections.flatMap((section) => {
                    const sectionName = section.key === 'main'
                        ? null
                        : this.$t(`team.sections_details.${section.key}.name`)

                    return section.coordinators.map((person, personIndex) => {
                        const contact = this.contacts[person.name] || {}
                        const roleLabel = this.$t(`team.roles.${person.roleKey}`)
                        return {
                            id: `${section.key}-${personIndex}`,
                            photo: this.photos[person.name],
                            name: person.name,
                            role: sectionName ? `${roleLabel} · ${sectionName}` : roleLabel,
                            tel: contact.tel || null,
                            mail: contact.mail || null
                        }
                    })
                })
            }
        }
    }
</script>

<style lang="sass" scoped>
    #contact
        background: #faf7f1
        color: #0a0828
        font-family: Inter, sans-serif

    /* === HERO === */
    .contact-hero
        position: relative
        width: 100%
        min-height: 100vh
        background: #050316
        color: #fff
        display: flex
        flex-direction: column
        justify-content: space-between
        padding: 110px 0 50px
        overflow: hidden

    .hero-bg
        position: absolute
        inset: 0
        background-image: url('~@/assets/img/components/contact/background.webp')
        background-size: cover
        background-position: center
        background-repeat: no-repeat
        filter: saturate(0.85) brightness(0.85) contrast(1.05)
        transform: scale(1.05)

    .hero-overlay
        position: absolute
        inset: 0
        background: radial-gradient(ellipse at 30% 0%, rgba(26, 22, 96, 0.55) 0%, rgba(10, 8, 40, 0.78) 55%, rgba(5, 3, 22, 0.92) 100%)

    .hero-grain
        position: absolute
        inset: 0
        opacity: 0.12
        mix-blend-mode: overlay
        pointer-events: none
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>")

    .hero-top
        position: relative
        z-index: 2
        display: flex
        align-items: center
        gap: 18px
        padding: 0 56px
        font-size: 12px
        font-weight: 500
        letter-spacing: 0.18em
        text-transform: uppercase
        color: rgba(255, 255, 255, 0.85)

        .hero-divider
            width: 28px
            height: 1px
            background: rgba(255, 255, 255, 0.5)

    .hero-content
        position: relative
        z-index: 2
        max-width: 1280px
        margin: 0 auto
        padding: 0 56px
        width: 100%

        .hero-eyebrow
            display: inline-block
            font-size: 12px
            font-weight: 500
            letter-spacing: 0.24em
            text-transform: uppercase
            color: rgba(255, 255, 255, 0.7)
            margin-bottom: 28px

        .hero-title
            font-family: Raleway, sans-serif
            font-weight: 900
            font-size: clamp(48px, 7.5vw, 110px)
            line-height: 1
            letter-spacing: -0.025em
            margin: 0 0 32px
            color: #fff
            text-transform: lowercase

        .hero-lead
            font-size: clamp(16px, 1.5vw, 19px)
            line-height: 1.6
            font-weight: 400
            color: rgba(255, 255, 255, 0.88)
            max-width: 720px
            margin: 0 0 44px
            text-indent: 0

        .hero-mail-cta
            display: inline-flex
            align-items: center
            gap: 16px
            padding: 18px 28px
            border: 1px solid rgba(255, 255, 255, 0.4)
            border-radius: 999px
            font-family: Inter, sans-serif
            font-weight: 500
            font-size: 16px
            letter-spacing: 0.02em
            color: #fff
            text-decoration: none
            transition: all 0.25s ease

            .cta-arrow
                font-size: 18px
                transition: transform 0.25s ease

            &:hover
                background: rgba(255, 255, 255, 0.08)
                border-color: rgba(255, 255, 255, 0.7)

                .cta-arrow
                    transform: translateX(6px)

    .hero-bottom
        position: relative
        z-index: 2
        display: flex
        justify-content: flex-end
        padding: 0 56px
        max-width: 1280px
        margin: 0 auto
        width: 100%

        .scroll-link
            display: inline-flex
            align-items: center
            gap: 12px
            font-size: 12px
            font-weight: 500
            letter-spacing: 0.18em
            text-transform: uppercase
            color: rgba(255, 255, 255, 0.85)
            text-decoration: none
            padding: 14px 22px
            border: 1px solid rgba(255, 255, 255, 0.4)
            border-radius: 999px
            transition: all 0.2s ease

            &:hover
                background: rgba(255, 255, 255, 0.08)
                border-color: rgba(255, 255, 255, 0.7)

    /* === BLOCKS === */
    .block-light
        background: #faf7f1
        color: #0a0828
        padding: 160px 0 140px

    .block-dark
        background: #0a0828
        color: #fff
        padding: 140px 0

    .block-container
        max-width: 1280px !important
        padding: 0 56px !important

    .section-meta
        display: flex
        align-items: center
        gap: 18px
        margin-bottom: 36px
        font-size: 12px
        font-weight: 500
        letter-spacing: 0.22em
        text-transform: uppercase
        color: #0a0828

        .chapter-num
            font-family: Raleway, sans-serif
            font-weight: 700
            font-size: 14px
            letter-spacing: 0.1em

        .chapter-divider
            width: 36px
            height: 1px
            background: #0a0828
            opacity: 0.4

        .chapter-eyebrow
            opacity: 0.85

        &.section-meta--light
            color: #fff

            .chapter-divider
                background: #fff
                opacity: 0.5

    .block-title
        font-family: Raleway, sans-serif
        font-weight: 900
        font-size: clamp(40px, 5.5vw, 80px)
        line-height: 0.98
        letter-spacing: -0.025em
        margin: 0
        color: #0a0828
        text-transform: lowercase

        &.block-title--light
            color: #fff
            margin-bottom: 80px

    .coords-header
        display: grid
        grid-template-columns: 1.4fr 1fr
        gap: 80px
        align-items: end
        margin-bottom: 96px

        .coords-lead
            font-size: clamp(15px, 1.1vw, 17px)
            line-height: 1.7
            color: rgba(10, 8, 40, 0.7)
            margin: 0 0 8px
            text-indent: 0
            max-width: 460px

    /* === COORDINATORS GRID === */
    .coords-grid
        display: grid
        grid-template-columns: repeat(4, 1fr)
        gap: 56px 32px

    .coord-card
        display: flex
        flex-direction: column

        .coord-photo
            position: relative
            aspect-ratio: 3 / 4
            overflow: hidden
            margin-bottom: 24px

            img
                width: 100%
                height: 100%
                object-fit: cover
                object-position: center top
                display: block
                transition: transform 0.5s ease

        &:hover .coord-photo img
            transform: scale(1.04)

        .coord-info
            display: flex
            flex-direction: column
            gap: 6px

            .coord-role
                font-size: 11px
                font-weight: 500
                letter-spacing: 0.22em
                text-transform: uppercase
                color: rgba(10, 8, 40, 0.55)

            .coord-name
                font-family: Raleway, sans-serif
                font-weight: 800
                font-size: clamp(20px, 1.6vw, 24px)
                line-height: 1.15
                letter-spacing: -0.015em
                color: #0a0828
                margin: 0 0 6px

            .coord-link, .coord-tel
                font-size: 14px
                line-height: 1.5
                color: rgba(10, 8, 40, 0.78)
                text-decoration: none
                word-break: break-word
                border-bottom: 1px solid transparent
                transition: border-color 0.2s ease
                width: fit-content

                &:hover
                    border-bottom-color: rgba(10, 8, 40, 0.6)

            .coord-tel
                color: rgba(10, 8, 40, 0.5)
                font-variant-numeric: tabular-nums

    /* === DIRECT CONTACT GRID === */
    .contact-grid
        display: grid
        grid-template-columns: repeat(3, 1fr)
        gap: 48px

    .contact-card
        display: flex
        flex-direction: column
        gap: 16px
        padding: 36px 0
        border-top: 1px solid rgba(255, 255, 255, 0.18)

        .card-label
            font-size: 11px
            font-weight: 600
            letter-spacing: 0.22em
            text-transform: uppercase
            color: rgba(255, 255, 255, 0.55)
            margin-bottom: 4px

        .card-value
            font-family: Raleway, sans-serif
            font-weight: 700
            font-size: clamp(20px, 1.6vw, 24px)
            line-height: 1.25
            letter-spacing: -0.01em
            color: #fff
            text-decoration: none
            word-break: break-word

        .card-link
            color: #fff
            text-decoration: none
            border-bottom: 1px solid transparent
            transition: border-color 0.2s ease

            &:hover
                border-bottom-color: #fff

        .card-socials
            display: flex
            flex-direction: column
            gap: 8px
            font-family: Raleway, sans-serif
            font-weight: 700
            font-size: clamp(20px, 1.6vw, 24px)
            line-height: 1.25
            letter-spacing: -0.01em

        .card-note
            font-size: 14px
            line-height: 1.6
            color: rgba(255, 255, 255, 0.6)
            margin: 0
            text-indent: 0

    /* === RESPONSIVE === */
    @media (max-width: 1100px)
        .contact-grid
            grid-template-columns: 1fr 1fr

        .coords-grid
            grid-template-columns: repeat(3, 1fr)

        .coords-header
            grid-template-columns: 1fr
            gap: 24px
            align-items: start

    @media (max-width: 800px)
        .contact-hero
            padding: 100px 0 30px

        .hero-bg
            transform: none

        .hero-top
            padding: 0 28px
            font-size: 11px
            gap: 14px

            .hero-divider--hide,
            .hero-since--hide
                display: none

        .hero-content
            padding: 0 28px

            .hero-title
                font-size: clamp(40px, 11vw, 68px)

            .hero-mail-cta
                font-size: 14px
                padding: 14px 22px

        .hero-bottom
            padding: 0 28px
            justify-content: center

        .block-light
            padding: 100px 0 90px

        .block-dark
            padding: 90px 0

        .block-container
            padding: 0 28px !important

        .coords-header
            margin-bottom: 64px

        .block-title.block-title--light
            margin-bottom: 56px

        .contact-grid
            grid-template-columns: 1fr
            gap: 0

        .coords-grid
            grid-template-columns: repeat(2, 1fr)
            gap: 36px 20px

    @media (max-width: 480px)
        .coords-grid
            grid-template-columns: 1fr
            gap: 32px
</style>
